// Where the playhead is, for the chat to send with a message.
//
// A motion-scene edit is anchored to a moment: "bu turuncu çok parlak" only
// resolves against the frame that was on screen when the user said it. Asking
// the model to guess the second, when the editor knows it exactly, is a way to
// be wrong for no reason — so the value travels on the selection descriptor
// and the agent never infers it.
//
// The source is the editor's own `aidea:char-time` tick (EditorApp.jsx), which
// carries `{ layerId, t, inRange }` with `t` in seconds RELATIVE TO THE BOX.
// That is exactly the clock a scene document is seeked on, so no conversion is
// needed anywhere.
//
// Only one value is kept, not one per layer. A scene that holds a motion scene
// holds nothing else that is time-driven: the modal only offers motionizing
// for a scene of narration and images (motionSceneEligibility), so there is
// never a second ticking box to confuse it with.

const LISTENING = "__aideaPlayheadListening";

let latest = null; // { layerId, tSec, at }

function onTick(e) {
  const d = e && e.detail;
  if (!d || typeof d.t !== "number" || !Number.isFinite(d.t)) return;
  // Out of range means the playhead is not over this box at all; its `t` is
  // meaningless and would place an edit at a moment the user never saw.
  if (d.inRange === false) return;
  latest = { layerId: d.layerId, tSec: d.t, at: Date.now() };
}

/** Idempotent — safe to call from every mount. */
export function startPlayheadTracking() {
  if (typeof window === "undefined" || window[LISTENING]) return;
  window[LISTENING] = true;
  window.addEventListener("aidea:char-time", onTick);
}

export function stopPlayheadTracking() {
  if (typeof window === "undefined" || !window[LISTENING]) return;
  window[LISTENING] = false;
  window.removeEventListener("aidea:char-time", onTick);
  latest = null;
}

/**
 * The last position the playhead ticked at, in seconds from the start of the
 * box it was over.
 *
 * Returns null when nothing has ticked, or when the last tick is older than
 * `maxAgeMs`. Staleness matters: ticks stop when playback is paused and the
 * scene is left, and sending a minute-old position would anchor an edit to a
 * frame from a scene the user has since navigated away from.
 */
export function latestPlayheadSec(maxAgeMs = 5 * 60 * 1000) {
  if (!latest) return null;
  if (Date.now() - latest.at > maxAgeMs) return null;
  return latest.tSec;
}

/** Test seam — drops the remembered position without touching the listener. */
export function resetPlayheadForTest() {
  latest = null;
}
